import { ReceiptState } from '@pflegenavi/shared/api';

/**
 * Calculates the number of receipts waiting before a pending receipt.
 *
 * Returns the count of receipts ahead in the queue (index position)
 * in the pending_receipts array (which should be sorted by submission_date ASC).
 *
 * @example
 * ```typescript
 * const waitingBefore = calculateQueuePosition(
 *   ReceiptState.Pending,
 *   [{ id: 'receipt-1' }, { id: 'receipt-2' }, { id: 'receipt-3' }],
 *   'receipt-2'
 * );
 * // Returns 1 (one receipt waiting before this one)
 * ```
 *
 * @param receiptState - Current state of the receipt
 * @param pendingReceipts - Array of pending receipts sorted by submission_date
 * @param receiptId - ID of the receipt to find
 * @returns Number of receipts waiting before this one, or undefined if not pending or not found
 */
export function calculateQueuePosition(
  receiptState: ReceiptState,
  pendingReceipts: Array<{ id: string }> | undefined,
  receiptId: string
): number | undefined {
  // Validate inputs
  if (!receiptId || !receiptId.trim()) {
    return undefined;
  }

  if (receiptState !== ReceiptState.Pending) {
    return undefined;
  }

  const receipts = pendingReceipts ?? [];
  const index = receipts.findIndex((r) => r.id === receiptId);

  return index >= 0 ? index : undefined;
}
